// AI Agent Tools - Functions the agent can call to take actions

import { leadsApi } from 'src/api/leads';
import { quotesApi } from 'src/api/quotes';
import type { LeadInput, QuoteInput, LeadV5 } from 'src/api/types';
import { getCompanyInfo } from 'src/api/supabase/company-settings';

// Tool definitions for the AI model
export const toolDefinitions = [
  {
    name: 'search_customer',
    description: 'Search for an existing customer by name, email, or phone number',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'The search query (name, email, or phone)'
        }
      },
      required: ['query']
    }
  },
  {
    name: 'create_customer',
    description: 'Create a new customer/lead. IMPORTANT: Include linearFootage and leadStatus if the user mentions footage or status.',
    parameters: {
      type: 'object',
      properties: {
        firstName: {
          type: 'string',
          description: 'Customer first name (required)'
        },
        lastName: {
          type: 'string',
          description: 'Customer last name (required)'
        },
        linearFootage: {
          type: 'number',
          description: 'IMPORTANT: Total linear footage for lighting project. If user says "150ft" pass 150.'
        },
        leadStatus: {
          type: 'string',
          enum: ['facebook_new', 'warm', 'hot', 'closed', 'dead', 'paid', 'lead_2025', 'lead_2026'],
          description: 'IMPORTANT: Lead status. If user says "warm" pass "warm".'
        },
        email: {
          type: 'string',
          description: 'Customer email address'
        },
        phone: {
          type: 'string',
          description: 'Customer phone number'
        },
        address: {
          type: 'string',
          description: 'Customer street address'
        },
        city: {
          type: 'string',
          description: 'Customer city'
        },
        state: {
          type: 'string',
          description: 'Customer state (e.g., WA)'
        },
        zipCode: {
          type: 'string',
          description: 'Customer zip code'
        },
        linearFootageFront: {
          type: 'number',
          description: 'Linear footage for front of house'
        },
        linearFootageBack: {
          type: 'number',
          description: 'Linear footage for back of house'
        },
        linearFootageLeft: {
          type: 'number',
          description: 'Linear footage for left side of house'
        },
        linearFootageRight: {
          type: 'number',
          description: 'Linear footage for right side of house'
        },
        projectNotes: {
          type: 'string',
          description: 'Notes about the project'
        }
      },
      required: ['firstName', 'lastName']
    }
  },
  {
    name: 'update_customer',
    description: 'Update an existing customer/lead',
    parameters: {
      type: 'object',
      properties: {
        customerId: {
          type: 'string',
          description: 'The customer ID (documentId)'
        },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        email: { type: 'string' },
        phone: { type: 'string' },
        linearFootage: { type: ['number', 'string'] },
        linearFootageFront: { type: ['number', 'string'] },
        linearFootageBack: { type: ['number', 'string'] },
        linearFootageLeft: { type: ['number', 'string'] },
        linearFootageRight: { type: ['number', 'string'] },
        projectNotes: { type: 'string' },
        leadStatus: {
          type: 'string',
          enum: ['facebook_new', 'warm', 'hot', 'closed', 'dead', 'paid', 'lead_2025', 'lead_2026']
        }
      },
      required: ['customerId']
    }
  },
  {
    name: 'create_quote',
    description: 'Create a quote for a customer. Use after create_customer to link the quote. Price is $28/ft + $350 controller.',
    parameters: {
      type: 'object',
      properties: {
        customerId: {
          type: 'string',
          description: 'IMPORTANT: The customer documentId returned from create_customer. Required to link quote to customer.'
        },
        customerName: {
          type: 'string',
          description: 'Full name of the customer (e.g., "John Smith")'
        },
        linearFootage: {
          type: 'number',
          description: 'IMPORTANT: Total linear footage. If user says "150ft" pass 150.'
        },
        linearFootageFront: {
          type: 'number',
          description: 'Linear footage for front of house'
        },
        linearFootageBack: {
          type: 'number',
          description: 'Linear footage for back of house'
        },
        linearFootageLeft: {
          type: 'number',
          description: 'Linear footage for left side'
        },
        linearFootageRight: {
          type: 'number',
          description: 'Linear footage for right side'
        },
        pricePerFoot: {
          type: 'number',
          description: 'Price per linear foot (defaults to 28)'
        },
        includeController: {
          type: 'boolean',
          description: 'Include controller (defaults to true, adds $350)'
        },
        controllerPrice: {
          type: 'number',
          description: 'Controller price (defaults to 350)'
        },
        notes: {
          type: 'string',
          description: 'Additional notes for the quote'
        }
      },
      required: ['customerName', 'linearFootage']
    }
  },
  {
    name: 'get_customer_quotes',
    description: 'Get all quotes for a specific customer',
    parameters: {
      type: 'object',
      properties: {
        customerId: {
          type: 'string',
          description: 'The customer ID (documentId)'
        }
      },
      required: ['customerId']
    }
  },
  {
    name: 'get_recent_leads',
    description: 'Get a list of recent leads/customers',
    parameters: {
      type: 'object',
      properties: {
        status: {
          type: 'string',
          enum: ['facebook_new', 'warm', 'hot', 'closed', 'dead', 'paid', 'lead_2025', 'lead_2026'],
          description: 'Filter by lead status'
        },
        limit: {
          type: 'number',
          description: 'Number of leads to return (default 10)'
        }
      }
    }
  },
  {
    name: 'bulk_update_leads',
    description: 'Update multiple leads at once based on filter criteria. Use this to mass-update lead statuses (e.g., mark all leads except closed as dead).',
    parameters: {
      type: 'object',
      properties: {
        excludeStatuses: {
          type: 'array',
          items: { type: 'string' },
          description: 'Lead statuses to EXCLUDE from the update (e.g., ["closed", "paid"] to exclude closed/paid leads)'
        },
        includeStatuses: {
          type: 'array',
          items: { type: 'string' },
          description: 'Lead statuses to INCLUDE in the update (if not specified, includes all except excludeStatuses)'
        },
        newStatus: {
          type: 'string',
          enum: ['facebook_new', 'warm', 'hot', 'superhot', 'closed', 'dead', 'paid', 'lead_2025', 'lead_2026'],
          description: 'The new status to set for all matching leads'
        },
        confirmBulkUpdate: {
          type: 'boolean',
          description: 'Must be true to confirm the bulk update. Set to false to preview what would be updated.'
        }
      },
      required: ['newStatus', 'confirmBulkUpdate']
    }
  },
  {
    name: 'export_leads',
    description: 'Export all leads/customers to a CSV file for backup. Downloads a backup file with all lead data including name, contact info, address, footage, status, and notes.',
    parameters: {
      type: 'object',
      properties: {
        format: {
          type: 'string',
          enum: ['csv'],
          description: 'Export format (currently only csv supported)'
        }
      }
    }
  },
  {
    name: 'navigate_to_invoice',
    description: 'Navigate to the invoice creation page for a customer. Use this when the user wants to create a new invoice.',
    parameters: {
      type: 'object',
      properties: {
        customerName: {
          type: 'string',
          description: 'The customer name to create an invoice for (optional)'
        }
      }
    }
  },
  {
    name: 'navigate_to_agreement',
    description: 'Navigate to the signed agreements page. Use this when the user wants to create or view agreements.',
    parameters: {
      type: 'object',
      properties: {
        customerName: {
          type: 'string',
          description: 'The customer name to create an agreement for (optional)'
        }
      }
    }
  },
  {
    name: 'send_text_message',
    description: 'Navigate to the send texts page with a pre-filled message for a customer. Use this when the user wants to send a text message to someone.',
    parameters: {
      type: 'object',
      properties: {
        recipientName: {
          type: 'string',
          description: 'The name of the person to send the text to (required)'
        },
        message: {
          type: 'string',
          description: 'The message to send (required)'
        }
      },
      required: ['recipientName', 'message']
    }
  },
  {
    name: 'send_email',
    description: 'Navigate to the send email page with pre-filled data. Use this when the user wants to send an email. IMPORTANT: If the user says "I want to send an email" without specifying details, you MUST ask follow-up questions BEFORE calling this tool. Ask: 1) Which customer are you emailing? (ask for first and last name), 2) What type of email? (quote, invoice, agreement, or regular text), 3) If regular text, what message do you want to send? Only call this tool AFTER you have gathered all the required information.',
    parameters: {
      type: 'object',
      properties: {
        recipientName: {
          type: 'string',
          description: 'The FULL NAME (first and last) of the customer to send the email to. REQUIRED - must ask the user if not provided.'
        },
        recipientEmail: {
          type: 'string',
          description: 'The email address of the recipient (optional, will search by name if not provided)'
        },
        emailType: {
          type: 'string',
          enum: ['quote', 'invoice', 'agreement', 'update', 'normal'],
          description: 'The type of email to send. REQUIRED - must ask the user: quote (send a quote PDF), invoice (send an invoice), agreement (send service agreement), update (follow-up email), normal (plain text email)'
        },
        designTemplate: {
          type: 'string',
          enum: ['clean', 'warm', 'dark', 'christmas'],
          description: 'The design template/theme for the email (optional). Options: clean, warm, dark, christmas'
        },
        subject: {
          type: 'string',
          description: 'The email subject line (optional)'
        },
        message: {
          type: 'string',
          description: 'The email body/message content. REQUIRED if emailType is "normal" - must ask the user what message they want to send.'
        }
      },
      required: ['recipientName', 'emailType']
    }
  },
  {
    name: 'customer_analysis',
    description: 'Generate a comprehensive Customer Analysis PDF report with all saved customer data including contact info, project details, quotes, financial data, and notes. Downloads the PDF immediately. Use this when the user says "customer analysis" or wants a full report/analysis of a customer.',
    parameters: {
      type: 'object',
      properties: {
        customerName: {
          type: 'string',
          description: 'The full name of the customer to analyze (first and last name). REQUIRED.'
        }
      },
      required: ['customerName']
    }
  },
  {
    name: 'quarterly_analytics',
    description: 'Generate a comprehensive multi-page Quarterly Business Analytics PDF report with revenue, profit, sales metrics, customer data, and performance insights for the current quarter. Downloads the PDF immediately. Use this when the user says "quarterly analytics", "quarterly report", "Q1/Q2/Q3/Q4 analytics", or wants business performance data.',
    parameters: {
      type: 'object',
      properties: {
        quarter: {
          type: 'string',
          enum: ['Q1', 'Q2', 'Q3', 'Q4', 'current'],
          description: 'The quarter to analyze. Use "current" for the current quarter, or specify Q1, Q2, Q3, Q4. Defaults to current quarter.'
        },
        year: {
          type: 'number',
          description: 'The year for the report. Defaults to current year (2026).'
        }
      }
    }
  },
  {
    name: 'query_leads_database',
    description: 'Query the leads/customers database to get business data and statistics. Use this to answer questions about leads, customers, pipeline, revenue, and sales data.',
    parameters: {
      type: 'object',
      properties: {
        queryType: {
          type: 'string',
          enum: ['count_by_status', 'revenue_summary', 'pipeline_value', 'recent_activity', 'top_customers', 'monthly_stats', 'all_leads'],
          description: 'Type of query: count_by_status (leads by status), revenue_summary (total revenue), pipeline_value (potential revenue), recent_activity (recent leads), top_customers (highest value), monthly_stats (monthly breakdown), all_leads (all lead data)'
        },
        status: {
          type: 'string',
          enum: ['facebook_new', 'warm', 'hot', 'superhot', 'closed', 'dead', 'paid', 'lead_2025', 'lead_2026'],
          description: 'Filter by specific lead status (optional)'
        },
        limit: {
          type: 'number',
          description: 'Number of results to return (default 10)'
        }
      },
      required: ['queryType']
    }
  },
  {
    name: 'query_quotes_database',
    description: 'Query the quotes database to get quote statistics and data.',
    parameters: {
      type: 'object',
      properties: {
        queryType: {
          type: 'string',
          enum: ['recent_quotes', 'quotes_by_status', 'total_value', 'pending_quotes'],
          description: 'Type of query: recent_quotes, quotes_by_status, total_value, pending_quotes'
        },
        status: {
          type: 'string',
          enum: ['draft', 'sent', 'accepted', 'declined', 'expired'],
          description: 'Filter by quote status (optional)'
        },
        limit: {
          type: 'number',
          description: 'Number of results to return (default 10)'
        }
      },
      required: ['queryType']
    }
  },
  {
    name: 'query_installations_database',
    description: 'Query the installations database to get upcoming and completed installations.',
    parameters: {
      type: 'object',
      properties: {
        queryType: {
          type: 'string',
          enum: ['upcoming', 'completed', 'by_crew', 'this_week', 'this_month'],
          description: 'Type of query: upcoming (future installs), completed, by_crew, this_week, this_month'
        },
        limit: {
          type: 'number',
          description: 'Number of results to return (default 10)'
        }
      },
      required: ['queryType']
    }
  }
];

// Tool execution functions
export async function executeTool(toolName: string, params: Record<string, unknown>): Promise<{
  success: boolean;
  data?: unknown;
  error?: string;
}> {
  try {
    switch (toolName) {
      case 'search_customer': {
        const query = params.query as string;
        const response = await leadsApi.search(query, 1, 10);
        const leads = response.data.map((lead: any) => {
          if (lead.attributes) {
            return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
          }
          return lead;
        });
        return {
          success: true,
          data: {
            count: leads.length,
            customers: leads.map((lead: LeadV5) => {
              const customerId = lead.documentId || lead.id;
              return {
                id: customerId,
                name: `${lead.firstName} ${lead.lastName}`,
                email: lead.email,
                phone: lead.phone,
                address: lead.address,
                city: lead.city,
                state: lead.state,
                linearFootage: lead.linearFootage,
                status: lead.leadStatus,
                url: `/dashboard/customers/${customerId}`
              };
            })
          }
        };
      }

      case 'create_customer': {
        // Helper to parse number from string or number
        const parseNum = (val: unknown): number | undefined => {
          if (val === undefined || val === null) return undefined;
          const num = typeof val === 'string' ? parseFloat(val) : val as number;
          return isNaN(num) ? undefined : num;
        };

        // Log all received params for debugging
        // console.log('create_customer received params:', JSON.stringify(params, null, 2));

        const leadInput: LeadInput = {
          firstName: params.firstName as string,
          lastName: params.lastName as string,
          email: params.email as string | undefined,
          phone: params.phone as string | undefined,
          address: params.address as string | undefined,
          city: params.city as string | undefined,
          state: (params.state as string) || 'WA',
          zipCode: params.zipCode as string | undefined,
          linearFootage: parseNum(params.linearFootage),
          linearFootageFront: parseNum(params.linearFootageFront),
          linearFootageBack: parseNum(params.linearFootageBack),
          linearFootageLeft: parseNum(params.linearFootageLeft),
          linearFootageRight: parseNum(params.linearFootageRight),
          projectNotes: params.projectNotes as string | undefined,
          leadStatus: (params.leadStatus as LeadInput['leadStatus']) || 'warm',
          depositPaid: false,
          preferredContactMethod: 'phone'
        };

        const response = await leadsApi.create(leadInput);
        const newLead = response.data as unknown as LeadV5;

        const customerUrl = `/dashboard/customers/${newLead.documentId || newLead.id}`;

        return {
          success: true,
          data: {
            message: `Customer "${params.firstName} ${params.lastName}" created successfully. [View Customer](${customerUrl})`,
            customerId: newLead.documentId || newLead.id,
            customerUrl,
            customer: {
              id: newLead.documentId || newLead.id,
              name: `${newLead.firstName} ${newLead.lastName}`,
              linearFootage: newLead.linearFootage
            }
          }
        };
      }

      case 'update_customer': {
        const { customerId, ...updateData } = params;
        await leadsApi.update(customerId as string, updateData as Partial<LeadInput>);
        const customerUrl = `/dashboard/customers/${customerId}`;

        return {
          success: true,
          data: {
            message: `Customer updated successfully. [View Customer](${customerUrl})`,
            customerId,
            customerUrl
          }
        };
      }

      case 'create_quote': {
        // Helper to parse number from string or number
        const parseNum = (val: unknown, defaultVal = 0): number => {
          if (val === undefined || val === null) return defaultVal;
          const num = typeof val === 'string' ? parseFloat(val) : val as number;
          return isNaN(num) ? defaultVal : num;
        };

        // Calculate total footage
        let totalFootage = parseNum(params.linearFootage, 0);
        if (!totalFootage) {
          totalFootage =
            parseNum(params.linearFootageFront) +
            parseNum(params.linearFootageBack) +
            parseNum(params.linearFootageLeft) +
            parseNum(params.linearFootageRight);
        }

        const pricePerFoot = parseNum(params.pricePerFoot, 28);
        const controllerPrice = parseNum(params.controllerPrice, 350);
        const includeController = params.includeController !== false;

        const subtotal = (totalFootage * pricePerFoot) + (includeController ? controllerPrice : 0);
        const taxRate = 0.101; // Washington state tax
        const taxAmount = subtotal * taxRate;
        const totalAmount = subtotal + taxAmount;
        const depositPercent = 0.5;
        const depositAmount = totalAmount * depositPercent;

        // Generate quote number
        const quoteNumber = await quotesApi.generateQuoteNumber();

        // Calculate dates
        const today = new Date();
        const validUntil = new Date(today);
        validUntil.setDate(validUntil.getDate() + 30);

        const quoteInput: QuoteInput = {
          quoteNumber,
          quoteDate: today.toISOString().split('T')[0],
          validUntil: validUntil.toISOString().split('T')[0],
          clientName: params.customerName as string,
          clientState: 'WA',
          projectState: 'WA',
          linearFootage: totalFootage,
          pricePerFoot,
          controllerPrice: includeController ? controllerPrice : 0,
          includePowerSupply: false,
          subtotal,
          taxRate,
          taxAmount,
          totalAmount,
          depositPercent,
          depositAmount,
          status: 'draft',
          // Only include lead if customerId is provided
          ...(params.customerId ? { lead: params.customerId as string } : {})
        };

        const response = await quotesApi.create(quoteInput);
        const customerUrl = params.customerId ? `/dashboard/customers/${params.customerId}` : null;

        return {
          success: true,
          data: {
            message: `Quote ${quoteNumber} created successfully for ${params.customerName}.${customerUrl ? ` [View Customer](${customerUrl})` : ''}`,
            quoteNumber,
            quoteId: (response.data as any).documentId || (response.data as any).id,
            customerUrl,
            summary: {
              linearFootage: totalFootage,
              pricePerFoot,
              subtotal: subtotal.toFixed(2),
              tax: taxAmount.toFixed(2),
              total: totalAmount.toFixed(2),
              deposit: depositAmount.toFixed(2)
            }
          }
        };
      }

      case 'get_customer_quotes': {
        const customerId = params.customerId as string;
        const response = await quotesApi.getByLead(customerId);
        return {
          success: true,
          data: {
            count: response.data.length,
            quotes: response.data.map((quote: any) => {
              const q = quote.attributes || quote;
              return {
                quoteNumber: q.quoteNumber,
                status: q.status,
                linearFootage: q.linearFootage,
                total: q.totalAmount,
                date: q.quoteDate
              };
            })
          }
        };
      }

      case 'get_recent_leads': {
        const status = params.status as string | undefined;
        const limit = (params.limit as number) || 10;

        const response = await leadsApi.getAll({
          leadStatus: status as any,
          pageSize: limit
        });

        const leads = response.data.map((lead: any) => {
          const l = lead.attributes || lead;
          const customerId = lead.documentId || lead.id;
          return {
            id: customerId,
            name: `${l.firstName} ${l.lastName}`,
            status: l.leadStatus,
            linearFootage: l.linearFootage,
            phone: l.phone,
            email: l.email,
            url: `/dashboard/customers/${customerId}`
          };
        });

        return {
          success: true,
          data: {
            count: leads.length,
            leads
          }
        };
      }

      case 'bulk_update_leads': {
        const excludeStatuses = (params.excludeStatuses as string[]) || [];
        const includeStatuses = params.includeStatuses as string[] | undefined;
        const newStatus = params.newStatus as string;
        const confirmBulkUpdate = params.confirmBulkUpdate as boolean;

        // Fetch all leads (up to 1000)
        const response = await leadsApi.getAll({ pageSize: 1000 });
        const allLeads = response.data.map((lead: any) => {
          const l = lead.attributes || lead;
          return {
            id: lead.documentId || lead.id,
            name: `${l.firstName} ${l.lastName}`,
            status: l.leadStatus
          };
        });

        // Filter leads based on criteria
        const leadsToUpdate = allLeads.filter((lead: { status: string }) => {
          // If includeStatuses is specified, only include those
          if (includeStatuses && includeStatuses.length > 0) {
            return includeStatuses.includes(lead.status);
          }
          // Otherwise, exclude the excludeStatuses
          return !excludeStatuses.includes(lead.status);
        });

        // If not confirmed, return preview
        if (!confirmBulkUpdate) {
          const statusCounts: Record<string, number> = {};
          leadsToUpdate.forEach((lead: { status: string }) => {
            statusCounts[lead.status] = (statusCounts[lead.status] || 0) + 1;
          });

          return {
            success: true,
            data: {
              preview: true,
              message: `Preview: ${leadsToUpdate.length} leads would be updated to "${newStatus}"`,
              totalLeads: allLeads.length,
              leadsToUpdate: leadsToUpdate.length,
              byCurrentStatus: statusCounts,
              excludedStatuses: excludeStatuses,
              sampleLeads: leadsToUpdate.slice(0, 5).map((l: { name: string; status: string }) => `${l.name} (${l.status})`)
            }
          };
        }

        // Perform the bulk update
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];

        for (const lead of leadsToUpdate) {
          try {
            await leadsApi.update(lead.id, { leadStatus: newStatus as any });
            successCount++;
          } catch (error) {
            errorCount++;
            if (errors.length < 3) {
              errors.push(`Failed to update ${lead.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }
        }

        return {
          success: errorCount === 0,
          data: {
            message: `Bulk update complete: ${successCount} leads updated to "${newStatus}"${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
            successCount,
            errorCount,
            newStatus,
            errors: errors.length > 0 ? errors : undefined
          }
        };
      }

      case 'export_leads': {
        // Fetch ALL leads (no pagination limit)
        const response = await leadsApi.getAll({ pageSize: 10000 });
        const leads = response.data;

        if (leads.length === 0) {
          return {
            success: false,
            error: 'No leads to export'
          };
        }

        // Define CSV headers
        const headers = [
          'ID',
          'First Name',
          'Last Name',
          'Email',
          'Phone',
          'Address',
          'City',
          'State',
          'Zip Code',
          'Lead Status',
          'Linear Footage',
          'Linear Footage Front',
          'Linear Footage Back',
          'Linear Footage Left',
          'Linear Footage Right',
          'Project Notes',
          'Preferred Contact',
          'Deposit Paid',
          'Created At',
          'Updated At'
        ];

        // Format lead data for CSV
        const rows = leads.map((lead: any) => {
          const l = lead.attributes || lead;
          return [
            l.documentId || l.id || '',
            l.firstName || '',
            l.lastName || '',
            l.email || '',
            l.phone || '',
            (l.address || '').replace(/,/g, ' '),
            l.city || '',
            l.state || '',
            l.zipCode || '',
            l.leadStatus || '',
            l.linearFootage || '',
            l.linearFootageFront || '',
            l.linearFootageBack || '',
            l.linearFootageLeft || '',
            l.linearFootageRight || '',
            (l.projectNotes || '').replace(/,/g, ' ').replace(/\n/g, ' '),
            l.preferredContactMethod || '',
            l.depositPaid ? 'Yes' : 'No',
            l.createdAt || '',
            l.updatedAt || ''
          ];
        });

        // Create CSV content
        const csvContent = [
          headers.join(','),
          ...rows.map((row: string[]) => row.join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const date = new Date().toISOString().split('T')[0];
        link.href = url;
        link.download = `leads-backup-${date}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return {
          success: true,
          data: {
            message: `Successfully exported ${leads.length} leads to CSV. The file "leads-backup-${date}.csv" has been downloaded.`,
            count: leads.length,
            filename: `leads-backup-${date}.csv`
          }
        };
      }

      case 'navigate_to_invoice': {
        // Navigate to the invoice creation page
        const customerName = params.customerName as string | undefined;

        // Store customer name in session storage for the invoice page to pick up
        if (customerName) {
          sessionStorage.setItem('relight_invoice_customer', JSON.stringify({ customerName }));
        }

        // Navigate using window.location since we don't have access to react-router here
        window.location.href = '/dashboard/invoice';

        return {
          success: true,
          data: {
            message: customerName
              ? `Opening invoice creation page for ${customerName}...`
              : 'Opening invoice creation page...',
            navigatedTo: '/dashboard/invoice'
          }
        };
      }

      case 'navigate_to_agreement': {
        // Navigate to the agreements page
        const customerName = params.customerName as string | undefined;

        // Store customer name in session storage for the agreements page to pick up
        if (customerName) {
          sessionStorage.setItem('relight_agreement_customer', JSON.stringify({ customerName }));
        }

        // Navigate using window.location
        window.location.href = '/dashboard/signed-agreements';

        return {
          success: true,
          data: {
            message: customerName
              ? `Opening agreements page for ${customerName}...`
              : 'Opening agreements page...',
            navigatedTo: '/dashboard/signed-agreements'
          }
        };
      }

      case 'send_text_message': {
        const recipientName = params.recipientName as string;
        const message = params.message as string;

        if (!recipientName || !message) {
          return {
            success: false,
            error: 'Both recipientName and message are required'
          };
        }

        // Store the text data in session storage for the send-texts page to pick up
        sessionStorage.setItem('relight_send_text_data', JSON.stringify({
          recipientName,
          message
        }));

        // Navigate to send-texts page
        window.location.href = '/dashboard/send-texts';

        return {
          success: true,
          data: {
            message: `Opening text message page to send "${message}" to ${recipientName}...`,
            navigatedTo: '/dashboard/send-texts',
            recipient: recipientName,
            textMessage: message
          }
        };
      }

      case 'send_email': {
        const recipientName = params.recipientName as string;
        const recipientEmail = params.recipientEmail as string | undefined;
        const emailType = (params.emailType as string) || 'normal';
        const designTemplate = params.designTemplate as string | undefined;
        const subject = params.subject as string | undefined;
        const message = params.message as string | undefined;

        if (!recipientName) {
          return {
            success: false,
            error: 'recipientName is required'
          };
        }

        // Store the email data in session storage for the send-email page to pick up
        sessionStorage.setItem('relight_send_email_data', JSON.stringify({
          recipientName,
          recipientEmail,
          emailType,
          designTemplate,
          subject,
          message
        }));

        // Navigate to send-email page
        window.location.href = '/dashboard/send-email';

        const emailTypeLabels: Record<string, string> = {
          quote: 'quote',
          invoice: 'invoice',
          agreement: 'agreement',
          update: 'update',
          normal: 'email'
        };

        return {
          success: true,
          data: {
            message: `Opening email composer to send ${emailTypeLabels[emailType] || 'email'} to ${recipientName}...`,
            navigatedTo: '/dashboard/send-email',
            recipient: recipientName,
            emailType
          }
        };
      }

      case 'customer_analysis': {
        const customerName = params.customerName as string;

        if (!customerName) {
          return {
            success: false,
            error: 'Customer name is required for analysis'
          };
        }

        // Search for the customer
        const searchResponse = await leadsApi.search(customerName, 1, 10);
        const leads = searchResponse.data.map((lead: any) => {
          if (lead.attributes) {
            return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
          }
          return lead;
        });

        if (leads.length === 0) {
          return {
            success: false,
            error: `No customer found with name "${customerName}"`
          };
        }

        // Get the first matching customer
        const customer = leads[0];
        const customerId = customer.documentId || customer.id;

        // Fetch quotes for this customer
        let customerQuotes: any[] = [];
        try {
          const quotesResponse = await quotesApi.getByLead(customerId);
          customerQuotes = quotesResponse.data || [];
        } catch (e) {
          // console.log('Could not fetch quotes:', e);
        }

        // Generate PDF using jsPDF
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let y = 20;

        // Helper function to add section
        const addSection = (title: string, content: string[][]) => {
          if (y > 250) {
            doc.addPage();
            y = 20;
          }
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(41, 112, 255);
          doc.text(title, margin, y);
          y += 8;
          doc.setDrawColor(41, 112, 255);
          doc.line(margin, y, pageWidth - margin, y);
          y += 8;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);

          content.forEach(([label, value]) => {
            if (y > 270) {
              doc.addPage();
              y = 20;
            }
            doc.setFont('helvetica', 'bold');
            doc.text(`${label}:`, margin, y);
            doc.setFont('helvetica', 'normal');
            const valueX = margin + 60;
            const maxWidth = pageWidth - valueX - margin;
            const lines = doc.splitTextToSize(value || 'N/A', maxWidth);
            doc.text(lines, valueX, y);
            y += lines.length * 5 + 3;
          });
          y += 5;
        };

        // Header
        doc.setFillColor(41, 112, 255);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('Customer Analysis Report', margin, 25);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, 35);
        y = 55;

        // Customer Name Header
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`${customer.firstName || ''} ${customer.lastName || ''}`, margin, y);
        y += 15;

        // Contact Information
        addSection('Contact Information', [
          ['Email', customer.email || 'Not provided'],
          ['Phone', customer.phone || 'Not provided'],
          ['Alt Phone', customer.alternatePhone || 'Not provided'],
          ['Preferred Contact', customer.preferredContactMethod || 'Not specified'],
          ['Best Time to Call', customer.bestTimeToCall || 'Not specified'],
        ]);

        // Address
        addSection('Address', [
          ['Street', customer.address || 'Not provided'],
          ['City', customer.city || 'Not provided'],
          ['State', customer.state || 'Not provided'],
          ['Zip Code', customer.zipCode || 'Not provided'],
        ]);

        // Project Details
        const totalFootage = (customer.linearFootage || 0) ||
          ((customer.linearFootageFront || 0) + (customer.linearFootageBack || 0) +
           (customer.linearFootageLeft || 0) + (customer.linearFootageRight || 0));

        addSection('Project Details', [
          ['Lead Status', customer.leadStatus || 'Unknown'],
          ['Lead Source', customer.leadSource || 'Not specified'],
          ['Project Type', customer.projectType || 'Not specified'],
          ['Customer Category', customer.customerCategory || 'Not specified'],
          ['Total Linear Footage', `${totalFootage} ft`],
          ['Front Footage', `${customer.linearFootageFront || 0} ft`],
          ['Back Footage', `${customer.linearFootageBack || 0} ft`],
          ['Left Footage', `${customer.linearFootageLeft || 0} ft`],
          ['Right Footage', `${customer.linearFootageRight || 0} ft`],
          ['Wants Landscape', customer.wantsLandscape ? 'Yes' : 'No'],
        ]);

        // Financial Information
        addSection('Financial Information', [
          ['Estimated Value', customer.estimatedValue ? `$${customer.estimatedValue.toLocaleString()}` : 'Not quoted'],
          ['Deposit Amount', customer.depositAmount ? `$${customer.depositAmount.toLocaleString()}` : '$0'],
          ['Deposit Paid', customer.depositPaid ? 'Yes' : 'No'],
          ['Deposit Date', customer.depositDate || 'N/A'],
          ['Balance Due', customer.balanceDue ? `$${customer.balanceDue.toLocaleString()}` : 'N/A'],
          ['Payment Method', customer.paymentMethod || 'Not specified'],
          ['Discount %', customer.discountPercent ? `${customer.discountPercent}%` : 'None'],
          ['Cash Discount', customer.cashDiscount ? `$${customer.cashDiscount}` : 'None'],
          ['Tax Rate', customer.taxRate ? `${customer.taxRate}%` : 'Default'],
        ]);

        // Product & Costs
        if (customer.trackQty || customer.ledSections || customer.accessoriesCost || customer.shippingCost || customer.employeeCost || customer.extraExpenses) {
          addSection('Product & Costs', [
            ['Track Quantity', customer.trackQty ? `${customer.trackQty}` : 'N/A'],
            ['LED Sections', customer.ledSections ? `${customer.ledSections}` : 'N/A'],
            ['Accessories Cost', customer.accessoriesCost ? `$${customer.accessoriesCost}` : 'N/A'],
            ['Shipping Cost', customer.shippingCost ? `$${customer.shippingCost}` : 'N/A'],
            ['Employee Cost', customer.employeeCost ? `$${customer.employeeCost}` : 'N/A'],
            ['Extra Expenses', customer.extraExpenses ? `$${customer.extraExpenses}` : 'N/A'],
          ]);
        }

        // Timeline & Follow-up
        addSection('Timeline & Follow-up', [
          ['Created', customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'Unknown'],
          ['Last Updated', customer.updatedAt ? new Date(customer.updatedAt).toLocaleDateString() : 'Unknown'],
          ['Last Contact', customer.lastContactDate || 'Not recorded'],
          ['Next Follow-up', customer.nextFollowUpDate || 'Not scheduled'],
          ['In-Person Quote Date', customer.inPersonQuoteDate || 'Not scheduled'],
          ['In-Person Quote Time', customer.inPersonQuoteTime || 'Not scheduled'],
          ['Est. Install Date', customer.estimatedInstallDate || 'Not scheduled'],
          ['Completion Date', customer.completionDate || 'Not completed'],
          ['Assigned Crew', customer.assignedCrew || 'Not assigned'],
        ]);

        // Notes
        if (customer.projectNotes || customer.followUpNotes) {
          addSection('Notes', [
            ['Project Notes', customer.projectNotes || 'None'],
            ['Follow-up Notes', customer.followUpNotes || 'None'],
          ]);
        }

        // Google Drive Links
        if (customer.googleDriveLinks && customer.googleDriveLinks.length > 0) {
          addSection('Saved Files', [
            ['Google Drive Links', customer.googleDriveLinks.join('\n') || 'None'],
          ]);
        }

        // Quotes Section
        if (customerQuotes.length > 0) {
          if (y > 200) {
            doc.addPage();
            y = 20;
          }
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(41, 112, 255);
          doc.text('Quotes History', margin, y);
          y += 8;
          doc.setDrawColor(41, 112, 255);
          doc.line(margin, y, pageWidth - margin, y);
          y += 10;

          customerQuotes.forEach((quote: any, index: number) => {
            const q = quote.attributes || quote;
            if (y > 240) {
              doc.addPage();
              y = 20;
            }

            doc.setFillColor(248, 250, 252);
            doc.roundedRect(margin, y - 3, pageWidth - 2 * margin, 45, 3, 3, 'F');

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(`Quote #${q.quoteNumber || index + 1}`, margin + 5, y + 5);

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Date: ${q.quoteDate || 'N/A'}`, margin + 5, y + 13);
            doc.text(`Status: ${q.status || 'Draft'}`, margin + 80, y + 13);
            doc.text(`Linear Footage: ${q.linearFootage || 0} ft`, margin + 5, y + 21);
            doc.text(`Price/Foot: $${q.pricePerFoot || 28}`, margin + 80, y + 21);
            doc.text(`Subtotal: $${(q.subtotal || 0).toLocaleString()}`, margin + 5, y + 29);
            doc.text(`Tax: $${(q.taxAmount || 0).toLocaleString()}`, margin + 80, y + 29);

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(41, 112, 255);
            doc.text(`Total: $${(q.totalAmount || 0).toLocaleString()}`, margin + 5, y + 37);
            doc.setTextColor(0, 0, 0);

            y += 52;
          });
        }

        // Footer
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(128, 128, 128);
          doc.text(
            `${getCompanyInfo().fullCompanyName || 'Relight Exterior Lighting'} - Customer Analysis Report - Page ${i} of ${totalPages}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }

        // Download the PDF
        const fileName = `Customer-Analysis-${customer.firstName}-${customer.lastName}-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return {
          success: true,
          data: {
            message: `Customer Analysis PDF for ${customer.firstName} ${customer.lastName} has been downloaded as "${fileName}"`,
            customer: {
              name: `${customer.firstName} ${customer.lastName}`,
              email: customer.email,
              phone: customer.phone,
              status: customer.leadStatus,
              linearFootage: totalFootage,
              estimatedValue: customer.estimatedValue
            },
            quotesCount: customerQuotes.length,
            fileName
          }
        };
      }

      case 'quarterly_analytics': {
        // Determine quarter and year
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const currentQuarter = Math.floor(currentMonth / 3) + 1;

        let targetQuarter = params.quarter as string || 'current';
        let targetYear = (params.year as number) || currentYear;

        if (targetQuarter === 'current') {
          targetQuarter = `Q${currentQuarter}`;
        }

        const quarterNum = parseInt(targetQuarter.replace('Q', ''));
        const quarterStartMonth = (quarterNum - 1) * 3;
        const quarterEndMonth = quarterStartMonth + 2;

        const quarterStart = new Date(targetYear, quarterStartMonth, 1);
        const quarterEnd = new Date(targetYear, quarterEndMonth + 1, 0, 23, 59, 59);

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const quarterMonths = [
          monthNames[quarterStartMonth],
          monthNames[quarterStartMonth + 1],
          monthNames[quarterStartMonth + 2]
        ];
        const shortMonths = quarterMonths.map(m => m.substring(0, 3));

        // Product costs
        const TRACK_PRICE = 12.53;
        const LED_SECTION_PRICE = 275;
        const CONTROLLER_500W_PRICE = 540;
        const CONTROLLER_350W_PRICE = 460;
        const PRICE_PER_FOOT = 27;
        const CONTROLLER_PRICE = 350;
        const POWER_SUPPLY_PRICE = 185;
        const DEFAULT_TAX_RATE = 10;

        // Fetch all closed leads
        const closedResponse = await leadsApi.getAll({ pageSize: 10000 });
        const allLeads = closedResponse.data.map((lead: any) => {
          if (lead.attributes) {
            return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
          }
          return lead;
        });

        // Filter for closed leads in the quarter
        const quarterLeads = allLeads.filter((lead: any) => {
          if (lead.leadStatus !== 'closed' && lead.leadStatus !== 'paid') return false;
          const leadDate = lead.updatedAt ? new Date(lead.updatedAt) :
                          lead.createdAt ? new Date(lead.createdAt) : null;
          if (!leadDate) return false;
          return leadDate >= quarterStart && leadDate <= quarterEnd;
        });

        // Calculate metrics
        let totalRevenue = 0;
        let totalProductCost = 0;
        let totalEmployeeCost = 0;
        let totalFootage = 0;
        let totalSalesTax = 0;
        const customerCategories: Record<string, number> = {};
        const leadSources: Record<string, number> = {};
        const monthlyData: Record<string, { revenue: number; projects: number; footage: number; profit: number; costs: number }> = {};

        // Initialize monthly data
        quarterMonths.forEach(month => {
          monthlyData[month] = { revenue: 0, projects: 0, footage: 0, profit: 0, costs: 0 };
        });

        quarterLeads.forEach((lead: any) => {
          const footage = lead.numOfFt || lead.linearFootage || 0;
          totalFootage += footage;

          // Product costs calculation
          const trackQty = lead.trackQty || (footage > 0 ? Math.ceil(footage / 7.5) : 0);
          const ledSections = lead.ledSections || (footage > 0 ? Math.ceil(footage / 37) : 0);
          const trackCost = trackQty * TRACK_PRICE;
          const ledCost = ledSections * LED_SECTION_PRICE;
          const controllerCost = footage >= 200 ? CONTROLLER_500W_PRICE : CONTROLLER_350W_PRICE;
          const accessoriesCost = lead.accessoriesCost || 0;
          const shippingCost = lead.shippingCost || 0;
          const productCost = trackCost + ledCost + controllerCost + accessoriesCost + shippingCost;
          totalProductCost += productCost;

          // Revenue calculation
          const basePrice = footage * PRICE_PER_FOOT;
          const discountPercent = lead.discountPercent || 0;
          const discountTotal = (basePrice * discountPercent) / 100;
          const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE - discountTotal;
          const customerTaxRate = (lead.taxRate ?? DEFAULT_TAX_RATE) / 100;
          const taxAmount = subtotal * customerTaxRate;
          const cashDiscount = lead.cashDiscount || 0;
          const revenue = subtotal + taxAmount - cashDiscount;
          totalRevenue += revenue;
          totalSalesTax += taxAmount;

          // Employee costs
          const employeeWages = lead.employeeCost || 0;
          const extraExpenses = lead.extraExpenses || 0;
          totalEmployeeCost += employeeWages + extraExpenses;

          // Categories
          const category = lead.customerCategory || 'uncategorized';
          customerCategories[category] = (customerCategories[category] || 0) + 1;

          // Lead sources
          const source = lead.leadSource || 'unknown';
          leadSources[source] = (leadSources[source] || 0) + 1;

          // Monthly breakdown
          const leadDate = lead.updatedAt ? new Date(lead.updatedAt) : new Date(lead.createdAt);
          const monthName = monthNames[leadDate.getMonth()];
          if (monthlyData[monthName]) {
            monthlyData[monthName].revenue += revenue;
            monthlyData[monthName].projects += 1;
            monthlyData[monthName].footage += footage;
            monthlyData[monthName].costs += productCost + employeeWages + extraExpenses;
            monthlyData[monthName].profit += revenue - productCost - employeeWages - extraExpenses;
          }
        });

        const grossProfit = totalRevenue - totalProductCost;
        const netProfit = grossProfit - totalEmployeeCost;
        const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
        const avgProjectValue = quarterLeads.length > 0 ? totalRevenue / quarterLeads.length : 0;
        const avgFootage = quarterLeads.length > 0 ? totalFootage / quarterLeads.length : 0;

        // Get all leads for pipeline analysis
        const allLeadsForPipeline = allLeads.filter((lead: any) => {
          const leadDate = lead.createdAt ? new Date(lead.createdAt) : null;
          if (!leadDate) return false;
          return leadDate >= quarterStart && leadDate <= quarterEnd;
        });

        const pipelineStatus: Record<string, number> = {};
        allLeadsForPipeline.forEach((lead: any) => {
          const status = lead.leadStatus || 'unknown';
          pipelineStatus[status] = (pipelineStatus[status] || 0) + 1;
        });

        // Fetch quotes for the quarter
        let quarterQuotes: any[] = [];
        try {
          const quotesResponse = await quotesApi.getAll({ pageSize: 10000 });
          quarterQuotes = (quotesResponse.data || []).filter((quote: any) => {
            const q = quote.attributes || quote;
            const quoteDate = q.createdAt ? new Date(q.createdAt) : null;
            if (!quoteDate) return false;
            return quoteDate >= quarterStart && quoteDate <= quarterEnd;
          });
        } catch (e) {
          // console.log('Could not fetch quotes:', e);
        }

        // Generate PDF
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;

        // Professional color palette
        const colors = {
          primary: [17, 24, 39],      // Charcoal
          secondary: [55, 65, 81],    // Dark gray
          accent: [37, 99, 235],      // Blue
          success: [22, 163, 74],     // Green
          warning: [234, 179, 8],     // Yellow
          danger: [220, 38, 38],      // Red
          light: [243, 244, 246],     // Light gray
          muted: [107, 114, 128],     // Muted gray
          white: [255, 255, 255],
        };

        // ========== HELPER FUNCTIONS FOR CHARTS ==========

        // Draw a horizontal bar chart
        const drawBarChart = (
          x: number, y: number, width: number, height: number,
          data: { label: string; value: number; color: number[] }[],
          title: string,
          showValues: boolean = true
        ) => {
          const maxValue = Math.max(...data.map(d => d.value), 1);
          const barHeight = (height - 25) / data.length - 8;
          const labelWidth = 45;
          const barAreaWidth = width - labelWidth - 40;

          // Title
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(title, x, y);

          let currentY = y + 12;

          data.forEach((item, i) => {
            // Label
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
            doc.text(item.label, x, currentY + barHeight / 2 + 1);

            // Background bar
            doc.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
            doc.roundedRect(x + labelWidth, currentY, barAreaWidth, barHeight, 2, 2, 'F');

            // Value bar
            const barWidth = maxValue > 0 ? (item.value / maxValue) * barAreaWidth : 0;
            doc.setFillColor(item.color[0], item.color[1], item.color[2]);
            doc.roundedRect(x + labelWidth, currentY, Math.max(barWidth, 2), barHeight, 2, 2, 'F');

            // Value text
            if (showValues) {
              doc.setFontSize(8);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
              const valueText = typeof item.value === 'number' && item.value >= 1000
                ? `$${(item.value / 1000).toFixed(1)}k`
                : item.value.toString();
              doc.text(valueText, x + labelWidth + barAreaWidth + 3, currentY + barHeight / 2 + 1);
            }

            currentY += barHeight + 8;
          });
        };

        // Draw a vertical bar chart
        const drawVerticalBarChart = (
          x: number, y: number, width: number, height: number,
          data: { label: string; value: number; color: number[] }[],
          title: string
        ) => {
          const maxValue = Math.max(...data.map(d => d.value), 1);
          const chartHeight = height - 35;
          const barWidth = (width - 20) / data.length - 10;
          const chartBottom = y + height - 15;

          // Title
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(title, x, y);

          // Y-axis line
          doc.setDrawColor(colors.light[0], colors.light[1], colors.light[2]);
          doc.setLineWidth(0.5);
          doc.line(x + 5, y + 15, x + 5, chartBottom);

          // X-axis line
          doc.line(x + 5, chartBottom, x + width - 5, chartBottom);

          // Y-axis labels (gridlines)
          const ySteps = 4;
          for (let i = 0; i <= ySteps; i++) {
            const yPos = chartBottom - (i / ySteps) * chartHeight;
            const value = (maxValue / ySteps) * i;

            // Gridline
            doc.setDrawColor(240, 240, 240);
            doc.line(x + 5, yPos, x + width - 5, yPos);

            // Label
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
            const labelText = value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toFixed(0);
            doc.text(labelText, x, yPos + 1, { align: 'left' });
          }

          // Bars
          data.forEach((item, i) => {
            const barX = x + 15 + i * (barWidth + 10);
            const barHeight = maxValue > 0 ? (item.value / maxValue) * chartHeight : 0;
            const barY = chartBottom - barHeight;

            // Bar
            doc.setFillColor(item.color[0], item.color[1], item.color[2]);
            doc.roundedRect(barX, barY, barWidth, barHeight, 2, 2, 'F');

            // Label
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
            doc.text(item.label, barX + barWidth / 2, chartBottom + 8, { align: 'center' });

            // Value on top
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
            const valueText = item.value >= 1000 ? `$${(item.value / 1000).toFixed(1)}k` : `$${item.value.toFixed(0)}`;
            doc.text(valueText, barX + barWidth / 2, barY - 2, { align: 'center' });
          });
        };

        // Draw a donut/pie chart
        const drawDonutChart = (
          centerX: number, centerY: number, outerRadius: number, innerRadius: number,
          data: { label: string; value: number; color: number[] }[],
          title: string
        ) => {
          const total = data.reduce((sum, d) => sum + d.value, 0);
          if (total === 0) return;

          // Title
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(title, centerX - outerRadius, centerY - outerRadius - 8);

          let startAngle = -Math.PI / 2; // Start from top

          data.forEach((item) => {
            const sliceAngle = (item.value / total) * 2 * Math.PI;
            const endAngle = startAngle + sliceAngle;

            // Draw slice using lines (pie approximation)
            doc.setFillColor(item.color[0], item.color[1], item.color[2]);

            // Create path for slice
            const segments = Math.max(Math.ceil(sliceAngle * 20), 2);
            const angleStep = sliceAngle / segments;

            // Build path points
            const points: [number, number][] = [];

            // Outer arc
            for (let i = 0; i <= segments; i++) {
              const angle = startAngle + i * angleStep;
              points.push([
                centerX + Math.cos(angle) * outerRadius,
                centerY + Math.sin(angle) * outerRadius
              ]);
            }

            // Inner arc (reverse)
            for (let i = segments; i >= 0; i--) {
              const angle = startAngle + i * angleStep;
              points.push([
                centerX + Math.cos(angle) * innerRadius,
                centerY + Math.sin(angle) * innerRadius
              ]);
            }

            // Draw filled polygon
            if (points.length >= 3) {
              const firstPoint = points[0];
              doc.setFillColor(item.color[0], item.color[1], item.color[2]);

              // Use triangles to fill the shape
              for (let i = 1; i < points.length - 1; i++) {
                doc.triangle(
                  firstPoint[0], firstPoint[1],
                  points[i][0], points[i][1],
                  points[i + 1][0], points[i + 1][1],
                  'F'
                );
              }
            }

            startAngle = endAngle;
          });

          // Center text
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(total.toString(), centerX, centerY + 2, { align: 'center' });
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
          doc.text('TOTAL', centerX, centerY + 8, { align: 'center' });

          // Legend
          let legendY = centerY - outerRadius + 5;
          const legendX = centerX + outerRadius + 10;

          data.slice(0, 5).forEach((item) => {
            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(0) : '0';

            // Color box
            doc.setFillColor(item.color[0], item.color[1], item.color[2]);
            doc.rect(legendX, legendY - 3, 4, 4, 'F');

            // Label
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
            const labelText = item.label.length > 12 ? item.label.substring(0, 12) + '...' : item.label;
            doc.text(`${labelText} (${percentage}%)`, legendX + 6, legendY);

            legendY += 10;
          });
        };

        // Draw a metric card
        const drawMetricCard = (
          x: number, y: number, width: number, height: number,
          label: string, value: string, subtext: string,
          accentColor: number[]
        ) => {
          // Card background
          doc.setFillColor(colors.white[0], colors.white[1], colors.white[2]);
          doc.roundedRect(x, y, width, height, 3, 3, 'F');

          // Left accent bar
          doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
          doc.rect(x, y, 3, height, 'F');

          // Border
          doc.setDrawColor(colors.light[0], colors.light[1], colors.light[2]);
          doc.setLineWidth(0.3);
          doc.roundedRect(x, y, width, height, 3, 3, 'S');

          // Label
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
          doc.text(label.toUpperCase(), x + 8, y + 10);

          // Value
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(value, x + 8, y + 24);

          // Subtext
          if (subtext) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
            doc.text(subtext, x + 8, y + 32);
          }
        };

        // Draw section header
        const drawSectionHeader = (y: number, title: string): number => {
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(title, margin, y);

          doc.setDrawColor(colors.accent[0], colors.accent[1], colors.accent[2]);
          doc.setLineWidth(0.8);
          doc.line(margin, y + 3, margin + 40, y + 3);

          return y + 12;
        };

        // ========== PAGE 1: Executive Summary ==========

        // Header band
        doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        doc.rect(0, 0, pageWidth, 45, 'F');

        // Company name
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
        doc.text('RELIGHT EXTERIOR LIGHTING', margin, 15);

        // Report title
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
        doc.text('Quarterly Analytics Report', margin, 30);

        // Period
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(200, 200, 200);
        doc.text(`${targetQuarter} ${targetYear}  |  ${quarterMonths[0]} - ${quarterMonths[2]}`, margin, 40);

        let y = 58;

        // Key Performance Indicators row
        const kpiWidth = (pageWidth - 2 * margin - 15) / 4;
        const kpiHeight = 38;

        drawMetricCard(margin, y, kpiWidth, kpiHeight,
          'Total Revenue', `$${Math.round(totalRevenue).toLocaleString()}`,
          `${quarterLeads.length} projects`, colors.accent);

        drawMetricCard(margin + kpiWidth + 5, y, kpiWidth, kpiHeight,
          'Net Profit', `$${Math.round(netProfit).toLocaleString()}`,
          `${profitMargin.toFixed(1)}% margin`, colors.success);

        drawMetricCard(margin + (kpiWidth + 5) * 2, y, kpiWidth, kpiHeight,
          'Total Footage', `${totalFootage.toLocaleString()} ft`,
          `${avgFootage.toFixed(0)} ft avg`, colors.warning);

        drawMetricCard(margin + (kpiWidth + 5) * 3, y, kpiWidth, kpiHeight,
          'Avg Project', `$${Math.round(avgProjectValue).toLocaleString()}`,
          'per installation', [139, 92, 246]);

        y += kpiHeight + 15;

        // Revenue & Profit Chart Section
        y = drawSectionHeader(y, 'Monthly Performance');

        // Monthly Revenue vs Profit bar chart
        const monthlyChartData = quarterMonths.map((month, i) => ({
          label: shortMonths[i],
          revenue: monthlyData[month].revenue,
          profit: monthlyData[month].profit,
          projects: monthlyData[month].projects
        }));

        drawVerticalBarChart(
          margin, y, (pageWidth - 2 * margin) * 0.55, 70,
          monthlyChartData.map((d, i) => ({
            label: d.label,
            value: d.revenue,
            color: [37, 99, 235]
          })),
          'Revenue by Month'
        );

        // Profit breakdown on the right
        const profitChartX = margin + (pageWidth - 2 * margin) * 0.6;
        drawVerticalBarChart(
          profitChartX, y, (pageWidth - 2 * margin) * 0.4, 70,
          monthlyChartData.map((d, i) => ({
            label: d.label,
            value: d.profit,
            color: [22, 163, 74]
          })),
          'Profit by Month'
        );

        y += 80;

        // Financial Summary Table
        y = drawSectionHeader(y, 'Financial Summary');

        // Table header
        const tableX = margin;
        const colWidths = [55, 35, 35, 35, 35];

        doc.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
        doc.rect(tableX, y, colWidths.reduce((a, b) => a + b, 0), 8, 'F');

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
        doc.text('METRIC', tableX + 3, y + 5.5);
        doc.text(shortMonths[0], tableX + colWidths[0] + 5, y + 5.5);
        doc.text(shortMonths[1], tableX + colWidths[0] + colWidths[1] + 5, y + 5.5);
        doc.text(shortMonths[2], tableX + colWidths[0] + colWidths[1] + colWidths[2] + 5, y + 5.5);
        doc.text('TOTAL', tableX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + 5, y + 5.5);

        y += 8;

        const tableRows = [
          { metric: 'Revenue', values: quarterMonths.map(m => monthlyData[m].revenue), total: totalRevenue },
          { metric: 'Costs (COGS + Labor)', values: quarterMonths.map(m => monthlyData[m].costs), total: totalProductCost + totalEmployeeCost },
          { metric: 'Net Profit', values: quarterMonths.map(m => monthlyData[m].profit), total: netProfit },
          { metric: 'Projects', values: quarterMonths.map(m => monthlyData[m].projects), total: quarterLeads.length },
          { metric: 'Footage Installed', values: quarterMonths.map(m => monthlyData[m].footage), total: totalFootage },
        ];

        tableRows.forEach((row, idx) => {
          if (idx % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(tableX, y, colWidths.reduce((a, b) => a + b, 0), 8, 'F');
          }

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
          doc.text(row.metric, tableX + 3, y + 5.5);

          row.values.forEach((val, i) => {
            const xPos = tableX + colWidths.slice(0, i + 1).reduce((a, b) => a + b, 0) + 5;
            const displayVal = row.metric.includes('Projects') || row.metric.includes('Footage')
              ? val.toLocaleString() + (row.metric.includes('Footage') ? '' : '')
              : `$${Math.round(val).toLocaleString()}`;
            doc.text(displayVal, xPos, y + 5.5);
          });

          // Total column (bold)
          doc.setFont('helvetica', 'bold');
          const totalXPos = tableX + colWidths.slice(0, 4).reduce((a, b) => a + b, 0) + 5;
          const totalDisplay = row.metric.includes('Projects') || row.metric.includes('Footage')
            ? row.total.toLocaleString() + (row.metric.includes('Footage') ? ' ft' : '')
            : `$${Math.round(row.total).toLocaleString()}`;
          doc.text(totalDisplay, totalXPos, y + 5.5);

          y += 8;
        });

        // ========== PAGE 2: Lead Analytics ==========
        doc.addPage();

        // Page header
        doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
        doc.text(`${targetQuarter} ${targetYear} - Lead & Customer Analytics`, margin, 14);

        y = 35;

        // Pipeline Status Section
        y = drawSectionHeader(y, 'Sales Pipeline');

        const statusLabels: Record<string, string> = {
          facebook_new: 'Facebook New',
          warm: 'Warm',
          hot: 'Hot',
          superhot: 'Super Hot',
          closed: 'Closed',
          paid: 'Paid',
          dead: 'Dead',
          lead_2025: '2025 Lead',
          lead_2026: '2026 Lead',
        };

        const statusColors: Record<string, number[]> = {
          facebook_new: [59, 130, 246],
          warm: [251, 146, 60],
          hot: [239, 68, 68],
          superhot: [185, 28, 28],
          closed: [22, 163, 74],
          paid: [16, 185, 129],
          dead: [156, 163, 175],
          lead_2025: [139, 92, 246],
          lead_2026: [236, 72, 153],
        };

        const sortedStatuses = Object.entries(pipelineStatus).sort((a, b) => b[1] - a[1]).slice(0, 6);
        const totalPipelineLeads = sortedStatuses.reduce((sum, [, count]) => sum + count, 0);

        // Donut chart for pipeline
        drawDonutChart(
          margin + 40, y + 45, 35, 18,
          sortedStatuses.map(([status, count]) => ({
            label: statusLabels[status] || status,
            value: count,
            color: statusColors[status] || [156, 163, 175]
          })),
          'Lead Status Distribution'
        );

        // Bar chart for pipeline on right side
        drawBarChart(
          pageWidth / 2 + 10, y, (pageWidth / 2) - margin - 10, 90,
          sortedStatuses.map(([status, count]) => ({
            label: statusLabels[status] || status,
            value: count,
            color: statusColors[status] || [156, 163, 175]
          })),
          'Leads by Status',
          true
        );

        y += 100;

        // Customer Categories & Lead Sources side by side
        y = drawSectionHeader(y, 'Customer Segments');

        const categoryLabels: Record<string, string> = {
          new_home_owner: 'New Homeowner',
          holiday_decorator: 'Holiday Decorator',
          new_family: 'New Family',
          commercial_property: 'Commercial',
          security_everyday: 'Security/Daily',
          uncategorized: 'Uncategorized'
        };

        const categoryColors: Record<string, number[]> = {
          new_home_owner: [59, 130, 246],
          holiday_decorator: [22, 163, 74],
          new_family: [251, 146, 60],
          commercial_property: [139, 92, 246],
          security_everyday: [20, 184, 166],
          uncategorized: [156, 163, 175]
        };

        const sortedCategories = Object.entries(customerCategories).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const totalCategorized = sortedCategories.reduce((sum, [, count]) => sum + count, 0);

        drawDonutChart(
          margin + 40, y + 40, 30, 15,
          sortedCategories.map(([cat, count]) => ({
            label: categoryLabels[cat] || cat,
            value: count,
            color: categoryColors[cat] || [156, 163, 175]
          })),
          'Customer Categories'
        );

        // Lead Sources
        const sourceLabels: Record<string, string> = {
          website: 'Website',
          referral: 'Referral',
          google: 'Google',
          facebook: 'Facebook',
          instagram: 'Instagram',
          nextdoor: 'Nextdoor',
          home_show: 'Home Show',
          yard_sign: 'Yard Sign',
          other: 'Other',
          unknown: 'Unknown'
        };

        const sourceColors: Record<string, number[]> = {
          website: [37, 99, 235],
          referral: [22, 163, 74],
          google: [234, 179, 8],
          facebook: [59, 130, 246],
          instagram: [236, 72, 153],
          nextdoor: [34, 197, 94],
          home_show: [251, 146, 60],
          yard_sign: [139, 92, 246],
          other: [107, 114, 128],
          unknown: [156, 163, 175]
        };

        const sortedSources = Object.entries(leadSources).sort((a, b) => b[1] - a[1]).slice(0, 5);

        drawDonutChart(
          pageWidth / 2 + 40, y + 40, 30, 15,
          sortedSources.map(([src, count]) => ({
            label: sourceLabels[src] || src,
            value: count,
            color: sourceColors[src] || [156, 163, 175]
          })),
          'Lead Sources'
        );

        y += 95;

        // ========== PAGE 3: Quote & Performance Metrics ==========
        doc.addPage();

        // Page header
        doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
        doc.text(`${targetQuarter} ${targetYear} - Quotes & Performance Metrics`, margin, 14);

        y = 35;

        // Quote Analytics
        y = drawSectionHeader(y, 'Quote Performance');

        const quoteStatusCount: Record<string, number> = {};
        let totalQuoteValue = 0;
        quarterQuotes.forEach((quote: any) => {
          const q = quote.attributes || quote;
          const status = q.status || 'draft';
          quoteStatusCount[status] = (quoteStatusCount[status] || 0) + 1;
          totalQuoteValue += q.totalAmount || 0;
        });

        const avgQuoteValue = quarterQuotes.length > 0 ? totalQuoteValue / quarterQuotes.length : 0;
        const acceptedQuotes = quoteStatusCount['accepted'] || 0;
        const conversionRate = quarterQuotes.length > 0 ? (acceptedQuotes / quarterQuotes.length) * 100 : 0;

        // Quote KPI cards
        const quoteKpiWidth = (pageWidth - 2 * margin - 10) / 3;

        drawMetricCard(margin, y, quoteKpiWidth, 35,
          'Quotes Generated', quarterQuotes.length.toString(),
          'this quarter', colors.accent);

        drawMetricCard(margin + quoteKpiWidth + 5, y, quoteKpiWidth, 35,
          'Quote Value', `$${Math.round(totalQuoteValue).toLocaleString()}`,
          `$${Math.round(avgQuoteValue).toLocaleString()} avg`, colors.success);

        drawMetricCard(margin + (quoteKpiWidth + 5) * 2, y, quoteKpiWidth, 35,
          'Conversion', `${conversionRate.toFixed(1)}%`,
          `${acceptedQuotes} accepted`, [139, 92, 246]);

        y += 50;

        // Unit Economics Section
        y = drawSectionHeader(y, 'Unit Economics');

        // Cost breakdown visualization
        const unitEconData = [
          { label: 'Revenue/ft', value: totalFootage > 0 ? totalRevenue / totalFootage : 0, color: colors.accent },
          { label: 'COGS/ft', value: totalFootage > 0 ? totalProductCost / totalFootage : 0, color: colors.danger },
          { label: 'Labor/ft', value: totalFootage > 0 ? totalEmployeeCost / totalFootage : 0, color: colors.warning },
          { label: 'Profit/ft', value: totalFootage > 0 ? netProfit / totalFootage : 0, color: colors.success },
        ];

        const econCardWidth = (pageWidth - 2 * margin - 15) / 4;
        unitEconData.forEach((item, i) => {
          const x = margin + i * (econCardWidth + 5);

          doc.setFillColor(colors.white[0], colors.white[1], colors.white[2]);
          doc.roundedRect(x, y, econCardWidth, 40, 3, 3, 'F');
          doc.setDrawColor(colors.light[0], colors.light[1], colors.light[2]);
          doc.roundedRect(x, y, econCardWidth, 40, 3, 3, 'S');

          doc.setFillColor(item.color[0], item.color[1], item.color[2]);
          doc.rect(x, y, 3, 40, 'F');

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
          doc.text(item.label.toUpperCase(), x + 8, y + 12);

          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
          doc.text(`$${item.value.toFixed(2)}`, x + 8, y + 28);
        });

        y += 55;

        // Profitability breakdown
        y = drawSectionHeader(y, 'Cost Structure Analysis');

        const costBreakdown = [
          { label: 'Product Costs', value: totalProductCost, color: [239, 68, 68] },
          { label: 'Labor Costs', value: totalEmployeeCost, color: [251, 146, 60] },
          { label: 'Sales Tax', value: totalSalesTax, color: [156, 163, 175] },
          { label: 'Net Profit', value: netProfit, color: [22, 163, 74] },
        ];

        drawBarChart(
          margin, y, pageWidth - 2 * margin, 70,
          costBreakdown.map(item => ({
            label: item.label,
            value: item.value,
            color: item.color
          })),
          'Revenue Distribution',
          true
        );

        y += 85;

        // Executive Summary Box
        doc.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
        doc.roundedRect(margin, y, pageWidth - 2 * margin, 45, 5, 5, 'F');

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        doc.text('Quarter Summary', margin + 10, y + 12);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
        const summaryParagraph = `${targetQuarter} ${targetYear} concluded with ${quarterLeads.length} completed installations totaling ${totalFootage.toLocaleString()} linear feet. Total revenue of $${Math.round(totalRevenue).toLocaleString()} with $${Math.round(netProfit).toLocaleString()} net profit (${profitMargin.toFixed(1)}% margin). Average project value was $${Math.round(avgProjectValue).toLocaleString()} with ${avgFootage.toFixed(0)} ft average footage per installation.`;
        const summaryLines = doc.splitTextToSize(summaryParagraph, pageWidth - 2 * margin - 20);
        doc.text(summaryLines, margin + 10, y + 24);

        // Footer on all pages
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);

          // Footer line
          doc.setDrawColor(colors.light[0], colors.light[1], colors.light[2]);
          doc.setLineWidth(0.5);
          doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
          doc.text(getCompanyInfo().fullCompanyName || 'Relight Exterior Lighting', margin, pageHeight - 8);
          doc.text(`${targetQuarter} ${targetYear} Analytics Report`, pageWidth / 2, pageHeight - 8, { align: 'center' });
          doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
        }

        // Download the PDF
        const fileName = `Quarterly-Analytics-${targetQuarter}-${targetYear}-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return {
          success: true,
          data: {
            message: `${targetQuarter} ${targetYear} Quarterly Analytics PDF has been downloaded as "${fileName}"`,
            quarter: `${targetQuarter} ${targetYear}`,
            period: `${quarterMonths[0]} - ${quarterMonths[2]} ${targetYear}`,
            summary: {
              totalRevenue: `$${totalRevenue.toLocaleString()}`,
              netProfit: `$${netProfit.toLocaleString()}`,
              profitMargin: `${profitMargin.toFixed(1)}%`,
              projectsCompleted: quarterLeads.length,
              totalFootage: `${totalFootage.toLocaleString()} ft`,
              quotesGenerated: quarterQuotes.length
            },
            fileName
          }
        };
      }

      case 'query_leads_database': {
        const queryType = params.queryType as string;
        const statusFilter = params.status as string | undefined;
        const limit = (params.limit as number) || 10;

        // Fetch all leads
        const leadsResponse = await leadsApi.getAll({ pageSize: 10000 });
        const allLeads = leadsResponse.data.map((lead: any) => {
          if (lead.attributes) {
            return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
          }
          return lead;
        });

        // Constants for revenue calculation
        const PRICE_PER_FOOT = 27;
        const CONTROLLER_PRICE = 350;
        const POWER_SUPPLY_PRICE = 185;
        const DEFAULT_TAX_RATE = 10;

        switch (queryType) {
          case 'count_by_status': {
            const statusCounts: Record<string, number> = {};
            allLeads.forEach((lead: any) => {
              const status = lead.leadStatus || 'unknown';
              statusCounts[status] = (statusCounts[status] || 0) + 1;
            });
            return {
              success: true,
              data: {
                totalLeads: allLeads.length,
                byStatus: statusCounts,
                summary: Object.entries(statusCounts)
                  .map(([status, count]) => `${status}: ${count}`)
                  .join(', ')
              }
            };
          }

          case 'revenue_summary': {
            const closedLeads = allLeads.filter((lead: any) =>
              lead.leadStatus === 'closed' || lead.leadStatus === 'paid'
            );

            let totalRevenue = 0;
            let totalFootage = 0;

            closedLeads.forEach((lead: any) => {
              const footage = lead.numOfFt || lead.linearFootage || 0;
              totalFootage += footage;
              const basePrice = footage * PRICE_PER_FOOT;
              const discountPercent = lead.discountPercent || 0;
              const discountTotal = (basePrice * discountPercent) / 100;
              const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE - discountTotal;
              const taxRate = (lead.taxRate ?? DEFAULT_TAX_RATE) / 100;
              const taxAmount = subtotal * taxRate;
              const cashDiscount = lead.cashDiscount || 0;
              totalRevenue += subtotal + taxAmount - cashDiscount;
            });

            return {
              success: true,
              data: {
                closedProjects: closedLeads.length,
                totalRevenue: `$${totalRevenue.toLocaleString()}`,
                totalFootage: `${totalFootage.toLocaleString()} ft`,
                avgProjectValue: `$${(closedLeads.length > 0 ? totalRevenue / closedLeads.length : 0).toLocaleString()}`,
                avgFootage: `${Math.round(closedLeads.length > 0 ? totalFootage / closedLeads.length : 0)} ft`
              }
            };
          }

          case 'pipeline_value': {
            const pipelineLeads = allLeads.filter((lead: any) =>
              ['warm', 'hot', 'superhot', 'facebook_new'].includes(lead.leadStatus)
            );

            let pipelineValue = 0;
            pipelineLeads.forEach((lead: any) => {
              const footage = lead.numOfFt || lead.linearFootage || 0;
              const basePrice = footage * PRICE_PER_FOOT;
              const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE;
              const taxAmount = subtotal * 0.10;
              pipelineValue += subtotal + taxAmount;
            });

            const byStatus: Record<string, { count: number; value: number }> = {};
            pipelineLeads.forEach((lead: any) => {
              const status = lead.leadStatus;
              const footage = lead.numOfFt || lead.linearFootage || 0;
              const basePrice = footage * PRICE_PER_FOOT;
              const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE;
              const value = subtotal * 1.10;
              if (!byStatus[status]) byStatus[status] = { count: 0, value: 0 };
              byStatus[status].count += 1;
              byStatus[status].value += value;
            });

            return {
              success: true,
              data: {
                totalPipelineLeads: pipelineLeads.length,
                totalPipelineValue: `$${pipelineValue.toLocaleString()}`,
                byStatus: Object.entries(byStatus).map(([status, data]) =>
                  `${status}: ${data.count} leads ($${data.value.toLocaleString()})`
                ).join(', ')
              }
            };
          }

          case 'recent_activity': {
            const sortedLeads = [...allLeads]
              .filter((lead: any) => statusFilter ? lead.leadStatus === statusFilter : true)
              .sort((a: any, b: any) => {
                const dateA = new Date(a.updatedAt || a.createdAt || 0);
                const dateB = new Date(b.updatedAt || b.createdAt || 0);
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, limit);

            return {
              success: true,
              data: {
                recentLeads: sortedLeads.map((lead: any) => ({
                  name: `${lead.firstName} ${lead.lastName}`,
                  status: lead.leadStatus,
                  footage: lead.linearFootage || lead.numOfFt || 0,
                  date: lead.updatedAt || lead.createdAt
                }))
              }
            };
          }

          case 'top_customers': {
            const closedLeads = allLeads.filter((lead: any) =>
              lead.leadStatus === 'closed' || lead.leadStatus === 'paid'
            );

            const leadsWithValue = closedLeads.map((lead: any) => {
              const footage = lead.numOfFt || lead.linearFootage || 0;
              const basePrice = footage * PRICE_PER_FOOT;
              const discountPercent = lead.discountPercent || 0;
              const discountTotal = (basePrice * discountPercent) / 100;
              const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE - discountTotal;
              const taxRate = (lead.taxRate ?? DEFAULT_TAX_RATE) / 100;
              const revenue = subtotal + (subtotal * taxRate);
              return { ...lead, revenue };
            });

            const topCustomers = leadsWithValue
              .sort((a: any, b: any) => b.revenue - a.revenue)
              .slice(0, limit);

            return {
              success: true,
              data: {
                topCustomers: topCustomers.map((lead: any) => ({
                  name: `${lead.firstName} ${lead.lastName}`,
                  revenue: `$${lead.revenue.toLocaleString()}`,
                  footage: lead.linearFootage || lead.numOfFt || 0,
                  city: lead.city
                }))
              }
            };
          }

          case 'monthly_stats': {
            const closedLeads = allLeads.filter((lead: any) =>
              lead.leadStatus === 'closed' || lead.leadStatus === 'paid'
            );

            const monthlyStats: Record<string, { projects: number; revenue: number; footage: number }> = {};

            closedLeads.forEach((lead: any) => {
              const date = new Date(lead.updatedAt || lead.createdAt);
              const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

              if (!monthlyStats[monthKey]) {
                monthlyStats[monthKey] = { projects: 0, revenue: 0, footage: 0 };
              }

              const footage = lead.numOfFt || lead.linearFootage || 0;
              const basePrice = footage * PRICE_PER_FOOT;
              const discountPercent = lead.discountPercent || 0;
              const discountTotal = (basePrice * discountPercent) / 100;
              const subtotal = basePrice + CONTROLLER_PRICE + POWER_SUPPLY_PRICE - discountTotal;
              const taxRate = (lead.taxRate ?? DEFAULT_TAX_RATE) / 100;
              const revenue = subtotal + (subtotal * taxRate);

              monthlyStats[monthKey].projects += 1;
              monthlyStats[monthKey].revenue += revenue;
              monthlyStats[monthKey].footage += footage;
            });

            const sortedMonths = Object.entries(monthlyStats)
              .sort((a, b) => b[0].localeCompare(a[0]))
              .slice(0, 12);

            return {
              success: true,
              data: {
                monthlyBreakdown: sortedMonths.map(([month, stats]) => ({
                  month,
                  projects: stats.projects,
                  revenue: `$${stats.revenue.toLocaleString()}`,
                  footage: `${stats.footage.toLocaleString()} ft`
                }))
              }
            };
          }

          case 'all_leads':
          default: {
            const filteredLeads = statusFilter
              ? allLeads.filter((lead: any) => lead.leadStatus === statusFilter)
              : allLeads;

            return {
              success: true,
              data: {
                totalLeads: filteredLeads.length,
                leads: filteredLeads.slice(0, limit).map((lead: any) => ({
                  id: lead.documentId || lead.id,
                  name: `${lead.firstName} ${lead.lastName}`,
                  status: lead.leadStatus,
                  footage: lead.linearFootage || lead.numOfFt || 0,
                  email: lead.email,
                  phone: lead.phone,
                  city: lead.city
                }))
              }
            };
          }
        }
      }

      case 'query_quotes_database': {
        const queryType = params.queryType as string;
        const statusFilter = params.status as string | undefined;
        const limit = (params.limit as number) || 10;

        // Fetch all quotes
        let allQuotes: any[] = [];
        try {
          const quotesResponse = await quotesApi.getAll({ pageSize: 10000 });
          allQuotes = (quotesResponse.data || []).map((quote: any) => {
            if (quote.attributes) {
              return { ...quote.attributes, id: quote.id, documentId: quote.documentId };
            }
            return quote;
          });
        } catch (e) {
          return { success: false, error: 'Failed to fetch quotes' };
        }

        switch (queryType) {
          case 'recent_quotes': {
            const sortedQuotes = [...allQuotes]
              .filter((q: any) => statusFilter ? q.status === statusFilter : true)
              .sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, limit);

            return {
              success: true,
              data: {
                recentQuotes: sortedQuotes.map((q: any) => ({
                  quoteNumber: q.quoteNumber,
                  clientName: q.clientName,
                  status: q.status,
                  totalAmount: `$${(q.totalAmount || 0).toLocaleString()}`,
                  footage: q.linearFootage,
                  date: q.quoteDate || q.createdAt
                }))
              }
            };
          }

          case 'quotes_by_status': {
            const statusCounts: Record<string, number> = {};
            allQuotes.forEach((q: any) => {
              const status = q.status || 'unknown';
              statusCounts[status] = (statusCounts[status] || 0) + 1;
            });

            return {
              success: true,
              data: {
                totalQuotes: allQuotes.length,
                byStatus: statusCounts
              }
            };
          }

          case 'total_value': {
            let totalValue = 0;
            let acceptedValue = 0;
            let pendingValue = 0;

            allQuotes.forEach((q: any) => {
              const amount = q.totalAmount || 0;
              totalValue += amount;
              if (q.status === 'accepted') acceptedValue += amount;
              if (q.status === 'draft' || q.status === 'sent') pendingValue += amount;
            });

            return {
              success: true,
              data: {
                totalQuoteValue: `$${totalValue.toLocaleString()}`,
                acceptedValue: `$${acceptedValue.toLocaleString()}`,
                pendingValue: `$${pendingValue.toLocaleString()}`,
                totalQuotes: allQuotes.length
              }
            };
          }

          case 'pending_quotes': {
            const pendingQuotes = allQuotes
              .filter((q: any) => q.status === 'draft' || q.status === 'sent')
              .sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, limit);

            return {
              success: true,
              data: {
                pendingQuotes: pendingQuotes.map((q: any) => ({
                  quoteNumber: q.quoteNumber,
                  clientName: q.clientName,
                  totalAmount: `$${(q.totalAmount || 0).toLocaleString()}`,
                  status: q.status,
                  validUntil: q.validUntil
                })),
                totalPending: allQuotes.filter((q: any) => q.status === 'draft' || q.status === 'sent').length
              }
            };
          }

          default:
            return { success: false, error: 'Unknown query type' };
        }
      }

      case 'query_installations_database': {
        const queryType = params.queryType as string;
        const limit = (params.limit as number) || 10;

        // Import installations API dynamically
        const { installationsApi } = await import('src/api/supabase/installations');

        let installations: any[] = [];
        try {
          const response = await installationsApi.getAll();
          installations = response.data || [];
        } catch (e) {
          return { success: false, error: 'Failed to fetch installations' };
        }

        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        switch (queryType) {
          case 'upcoming': {
            const upcoming = installations
              .filter((i: any) => new Date(i.date) >= now && i.status !== 'completed')
              .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .slice(0, limit);

            return {
              success: true,
              data: {
                upcomingInstallations: upcoming.map((i: any) => ({
                  date: i.date,
                  customerName: i.customerName,
                  address: i.address,
                  crew: i.crew,
                  footage: i.footage,
                  status: i.status
                }))
              }
            };
          }

          case 'completed': {
            const completed = installations
              .filter((i: any) => i.status === 'completed')
              .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, limit);

            return {
              success: true,
              data: {
                completedInstallations: completed.map((i: any) => ({
                  date: i.date,
                  customerName: i.customerName,
                  address: i.address,
                  crew: i.crew,
                  footage: i.footage
                })),
                totalCompleted: installations.filter((i: any) => i.status === 'completed').length
              }
            };
          }

          case 'by_crew': {
            const crewStats: Record<string, number> = {};
            installations.forEach((i: any) => {
              const crew = i.crew || 'unassigned';
              crewStats[crew] = (crewStats[crew] || 0) + 1;
            });

            return {
              success: true,
              data: {
                byCrew: crewStats,
                totalInstallations: installations.length
              }
            };
          }

          case 'this_week': {
            const thisWeek = installations.filter((i: any) => {
              const date = new Date(i.date);
              return date >= startOfWeek && date < endOfWeek;
            });

            return {
              success: true,
              data: {
                thisWeekInstallations: thisWeek.map((i: any) => ({
                  date: i.date,
                  customerName: i.customerName,
                  address: i.address,
                  crew: i.crew,
                  status: i.status
                })),
                count: thisWeek.length
              }
            };
          }

          case 'this_month': {
            const thisMonth = installations.filter((i: any) => {
              const date = new Date(i.date);
              return date >= startOfMonth && date <= endOfMonth;
            });

            return {
              success: true,
              data: {
                thisMonthInstallations: thisMonth.map((i: any) => ({
                  date: i.date,
                  customerName: i.customerName,
                  address: i.address,
                  crew: i.crew,
                  status: i.status
                })),
                count: thisMonth.length
              }
            };
          }

          default:
            return { success: false, error: 'Unknown query type' };
        }
      }

      default:
        return {
          success: false,
          error: `Unknown tool: ${toolName}`
        };
    }
  } catch (error) {
    // console.error(`Error executing tool ${toolName}:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An error occurred'
    };
  }
}

// Format tool definitions for Groq/OpenAI format
export function getToolsForAPI() {
  return toolDefinitions.map(tool => ({
    type: 'function' as const,
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters
    }
  }));
}

// Format tool definitions for Google Gemini format
export function getToolsForGemini() {
  return toolDefinitions.map(tool => {
    // Convert OpenAI-style schema to Gemini format
    const properties: Record<string, any> = {};

    for (const [key, value] of Object.entries(tool.parameters.properties)) {
      const prop = value as any;
      // Gemini doesn't support array types like ['number', 'string']
      // Use the first type if it's an array, or 'STRING' as fallback
      let type = 'STRING';
      if (prop.type) {
        if (Array.isArray(prop.type)) {
          type = prop.type[0] === 'number' ? 'NUMBER' : 'STRING';
        } else {
          type = prop.type.toUpperCase();
          if (type === 'INTEGER') type = 'NUMBER';
        }
      }

      properties[key] = {
        type,
        description: prop.description || ''
      };

      // Add enum if present
      if (prop.enum) {
        properties[key].enum = prop.enum;
      }
    }

    return {
      name: tool.name,
      description: tool.description,
      parameters: {
        type: 'OBJECT',
        properties,
        required: tool.parameters.required || []
      }
    };
  });
}
